import { safeWarning } from "@mgdx-libs/error-handler/lib/safeWarning";
import { clearAccessToken, getAccessTokenUid, getFirebaseAuth, subscribeAccessTokenSaved } from "@mgdx-libs/firebase";
import { logger } from "@mgdx-libs/logger";
import * as Sentry from "@sentry/react";
import { browserLocalPersistence, onAuthStateChanged, setPersistence, User } from "firebase/auth";
import { createContext, PropsWithChildren, type ReactNode, useEffect, useRef, useState } from "react";

import { clearServerState, resetServerState } from "../../queryClient";
import { currentUserStore } from "../../stores/currentUserStore";
import { clearPatientSessionState } from "../../utils/user/clearTokenAndState";

type FirebaseAuthProps = {
  user: User | null;
};
const initilaFirebaseAuthProps = {
  user: null,
};
const FirebaseAuthContext = createContext<FirebaseAuthProps>(initilaFirebaseAuthProps);

let isBooting = true;

const INITIALIZE_FIREBASE_AUTH_KEY = "bootingFirebaseAuth" as const;
const BOOTING_CHECK_DELAY_MS = 1000;

export type FirebaseAuthObserver = (user: User | null) => Promise<void>;

export const FirebaseAuthProvider = ({
  children,
  observer,
}: PropsWithChildren<{ observer: FirebaseAuthObserver; children: ReactNode }>) => {
  const [user, setUser] = useState<User | null>(null);
  const lastUidRef = useRef<string | null>(null);
  const isSwitchingUserRef = useRef(false);

  useEffect(() => {
    window?.localStorage?.setItem(INITIALIZE_FIREBASE_AUTH_KEY, "true");
  }, []);

  // ログアウトや切り替えから新しい患者のトークンが保存されるまでの間に、トークンなしで取得して失敗した結果を残さない
  useEffect(
    () =>
      subscribeAccessTokenSaved(() => {
        if (!isSwitchingUserRef.current) return;

        isSwitchingUserRef.current = false;
        resetServerState();
      }),
    []
  );

  useEffect(() => {
    // MCCM WebView 環境では IndexedDB AbortError 回避のため localStorage を使用する
    if (process.env.MGDX_APP_NAME === "matsukiyococokara-yqb") {
      (async () => {
        const firebaseAuth = getFirebaseAuth();
        try {
          await setPersistence(firebaseAuth, browserLocalPersistence);
        } catch (error) {
          logger.error("FirebaseAuthProvider: setPersistence failed", error);
          safeWarning("FirebaseAuthProvider: setPersistence failed", {
            page: "FirebaseAuthApp",
            feature: "setPersistenceMutation",
          });
        }
      })();
    }
  }, []);

  useEffect(() => {
    const firebaseAuth = getFirebaseAuth();

    return onAuthStateChanged(firebaseAuth, () => {
      const isInitializingFirebaseAuthKey = window.localStorage.getItem(INITIALIZE_FIREBASE_AUTH_KEY) !== "false";

      // このページでFirebaseの初期化が行われている場合
      if (isBooting && isInitializingFirebaseAuthKey) {
        setTimeout(() => {
          isBooting = false;
          window.localStorage.setItem(INITIALIZE_FIREBASE_AUTH_KEY, "false");
        }, BOOTING_CHECK_DELAY_MS);
      }
    });
  }, []);

  useEffect(() => {
    const firebaseAuth = getFirebaseAuth();

    return onAuthStateChanged(firebaseAuth, (user) => {
      if (process.env.MGDX_APP_NAME === "matsukiyococokara-yqb") {
        if (user === null) {
          Sentry.captureMessage("debug: firebase userなし");
        }
      }
      const isInitializingFirebaseAuthKey = window.localStorage.getItem(INITIALIZE_FIREBASE_AUTH_KEY) !== "false";
      const isBootingAnother = !isBooting && isInitializingFirebaseAuthKey;

      logger.debug(
        "useInitializeFirebaseAuth.onAuthStateChanged isBootingAnother: %s, user: %o",
        isBootingAnother,
        user
      );

      // 別タブなどでFirebaseの初期化が行われているためにuserがnullになった場合
      if (isBootingAnother && !user) return;

      // ログアウトや別の患者への切り替えで、前の患者の取得結果を次の患者に見せない。
      // 再読み込みの直後は前回の通知がないため、保存済みのトークンの持ち主と比べる
      const uid = user?.uid ?? null;
      const previousUid = lastUidRef.current ?? getAccessTokenUid() ?? null;
      if (previousUid && previousUid !== uid) {
        // 新しい患者のトークンが保存されるまでに、前の患者のトークンで取得しない
        clearAccessToken();
        clearServerState();
        // 新しい患者の情報の取得に失敗しても、前の患者の氏名や患者情報を表示しない
        currentUserStore.getState().actions.clearPatientInfo();
        // トークは新しい患者の情報を反映したときに有効になる
        clearPatientSessionState();
        isSwitchingUserRef.current = true;
      }
      lastUidRef.current = uid;

      setUser(user);
      observer(user);
    });
  }, [observer]);

  return (
    <FirebaseAuthContext.Provider
      value={{
        user,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
};
